// src/Contacts/contactsData.js
// Static seed data for the Contacts CRM preview — reshaped around how a
// contractor's relationships actually work:
//   • COMPANIES (relationships / "big dogs"): GCs, property managers, realtors,
//     facilities owners, HOAs, and homeowners — cultivated toward a finish line.
//   • PEOPLE: individuals you talk to. A person has an OPTIONAL company
//     affiliation and attaches to one or more PROJECTS. Third parties (inspector,
//     designer, geotech, a sub's engineer) are just people whose company isn't
//     one of your relationships. Conversations are logged on the PERSON and
//     tagged with the PROJECT — so one inspector can span many jobs.
//   • BIDS / JOBS: pipeline opportunities, each tagged with a SOURCE.
// No backend — purely to show the look + feel.

export const REL_STAGES = ['Prospect', 'Pursuing', 'Active Source', 'Dormant', 'Not a Fit'];
export const REL_STAGE_META = {
  'Prospect':      { dot: '#a1a1aa', chip: 'bg-zinc-500/15 text-zinc-300',     bar: 'from-zinc-500/40',    tip: 'Know of them — no contact yet' },
  'Pursuing':      { dot: '#818cf8', chip: 'bg-indigo-500/15 text-indigo-300', bar: 'from-indigo-500/50',  tip: 'Working on them — calls, bids, coffee' },
  'Active Source': { dot: '#34d399', chip: 'bg-emerald-500/15 text-emerald-300', bar: 'from-emerald-500/50', tip: 'Sends us work now' },
  'Dormant':       { dot: '#fbbf24', chip: 'bg-amber-500/15 text-amber-300',   bar: 'from-amber-500/50',   tip: 'Was producing, gone quiet — reach out' },
  'Not a Fit':     { dot: '#f87171', chip: 'bg-red-500/15 text-red-300',       bar: 'from-red-500/40',     tip: 'Not going to happen — leave it' },
};

export const TYPE_META = {
  'Contractor':        '🦺',
  'Property Management':       '🏢',
  'Realtor':                   '🔑',
  'Facilities / Building Owner': '🏬',
  'Government / Public Agency': '🏛️',
  'HOA':                       '🏘️',
  'Homeowner':                 '🏠',
  // Fellow trades / mutual-referral companies (plumbers, HVAC, roofers…) —
  // cultivated for the work you trade, not the work they hire you for.
  'Trade Partner':             '🤝',
};
export const REL_TYPES = Object.keys(TYPE_META);

// CSLB license classifications — shown when a relationship is a contractor,
// so you know exactly what kind of shop you're cultivating.
export const CSLB_CLASSES = [
  'A — General Engineering',
  'B — General Building',
  'B-2 — Residential Remodeling',
  'C-2 — Insulation & Acoustical',
  'C-4 — Boiler / Hot Water Heating',
  'C-5 — Framing & Rough Carpentry',
  'C-6 — Cabinet / Millwork / Finish Carpentry',
  'C-7 — Low Voltage Systems',
  'C-8 — Concrete',
  'C-9 — Drywall',
  'C-10 — Electrical',
  'C-11 — Elevator',
  'C-12 — Earthwork & Paving',
  'C-13 — Fencing',
  'C-15 — Flooring & Floor Covering',
  'C-16 — Fire Protection',
  'C-17 — Glazing',
  'C-20 — HVAC',
  'C-21 — Building Moving / Demolition',
  'C-22 — Asbestos Abatement',
  'C-23 — Ornamental Metal',
  'C-27 — Landscaping',
  'C-28 — Lock & Security Equipment',
  'C-29 — Masonry',
  'C-31 — Construction Zone Traffic Control',
  'C-32 — Parking & Highway Improvement',
  'C-33 — Painting & Decorating',
  'C-34 — Pipeline',
  'C-35 — Lathing & Plastering',
  'C-36 — Plumbing',
  'C-38 — Refrigeration',
  'C-39 — Roofing',
  'C-42 — Sanitation System',
  'C-43 — Sheet Metal',
  'C-45 — Sign',
  'C-46 — Solar',
  'C-47 — General Manufactured Housing',
  'C-50 — Reinforcing Steel',
  'C-51 — Structural Steel',
  'C-53 — Swimming Pool',
  'C-54 — Tile (Ceramic & Mosaic)',
  'C-55 — Water Conditioning',
  'C-57 — Well Drilling',
  'C-60 — Welding',
  'C-61 — Limited Specialty',
];

export const SOURCES = [
  'Referral', 'Repeat GC', 'Repeat client', 'PlanHub', 'ConstructionConnect',
  'BuildingConnected', 'Cold outreach', 'Vendor list', 'Website', 'Word of mouth',
];
export const SOURCE_TONE = {
  'Referral': 'bg-emerald-500/15 text-emerald-300', 'Repeat GC': 'bg-emerald-500/15 text-emerald-300',
  'Repeat client': 'bg-teal-500/15 text-teal-300', 'PlanHub': 'bg-orange-500/15 text-orange-300',
  'ConstructionConnect': 'bg-amber-500/15 text-amber-300', 'BuildingConnected': 'bg-yellow-500/15 text-yellow-300',
  'Cold outreach': 'bg-indigo-500/15 text-indigo-300', 'Vendor list': 'bg-cyan-500/15 text-cyan-300',
  'Website': 'bg-sky-500/15 text-sky-300', 'Word of mouth': 'bg-violet-500/15 text-violet-300',
};

export const CHANNELS = {
  call:      { label: 'Call',      icon: 'Phone',         tone: 'text-emerald-300', bg: 'bg-emerald-500/15' },
  voicemail: { label: 'Voicemail', icon: 'Voicemail',     tone: 'text-amber-300',   bg: 'bg-amber-500/15' },
  text:      { label: 'Text',      icon: 'MessageSquare', tone: 'text-sky-300',     bg: 'bg-sky-500/15' },
  email:     { label: 'Email',     icon: 'Mail',          tone: 'text-violet-300',  bg: 'bg-violet-500/15' },
  note:      { label: 'Note',      icon: 'StickyNote',    tone: 'text-zinc-300',    bg: 'bg-zinc-500/15' },
};

export const BID_STAGES = ['Lead', 'Bidding', 'Won', 'Scheduled', 'Lost'];
export const BID_STAGE_META = {
  'Lead':      { dot: '#a1a1aa', chip: 'bg-zinc-500/15 text-zinc-300',     bar: 'from-zinc-500/40' },
  'Bidding':   { dot: '#fbbf24', chip: 'bg-amber-500/15 text-amber-300',   bar: 'from-amber-500/50' },
  'Scheduled': { dot: '#818cf8', chip: 'bg-indigo-500/15 text-indigo-300', bar: 'from-indigo-500/50' },
  'Won':       { dot: '#34d399', chip: 'bg-emerald-500/15 text-emerald-300', bar: 'from-emerald-500/50' },
  'Lost':      { dot: '#f87171', chip: 'bg-red-500/15 text-red-300',       bar: 'from-red-500/40' },
};

// ---- Projects (to tag people + conversations against) ----
export const PROJECTS = [
  { id: 'p1', name: 'Midtown Duplex Build-out', po: '0102' },
  { id: 'p2', name: 'Summit Office TI', po: '0103' },
  { id: 'p3', name: 'Folsom Ridge Lighting', po: '0104' },
  { id: 'p4', name: 'Riverpark Mixed-Use', po: '0105' },
  { id: 'p5', name: 'Oak Park Tenant Turn', po: '0106' },
];
export const projectName = (id) => (PROJECTS.find(p => p.id === id) || {}).name || '';

const ago = (d = 0, h = 0) => { const x = new Date(); x.setDate(x.getDate() - d); x.setHours(x.getHours() - h); return x.toISOString(); };
const inDays = (d) => { const x = new Date(); x.setDate(x.getDate() + d); x.setHours(9, 0, 0, 0); return x.toISOString(); };
let aid = 0;
const a = (channel, direction, at, note, outcome) => ({ id: `a${++aid}`, channel, direction, at, note, outcome });
// person activity: also carries the project it was about
const pa = (channel, direction, at, note, outcome, projectId) => ({ id: `a${++aid}`, channel, direction, at, note, outcome, projectId });

// ======== COMPANIES (relationships) ========
export const SEED_CONTACTS = [
  {
    id: 'c1', company: 'Sacramento Premier Property Mgmt', contactName: 'Dana Whitfield', title: 'Maintenance Coordinator',
    type: 'Property Management', relStage: 'Active Source', source: 'Cold outreach', starred: true,
    phone: '916-555-0142', email: 'dana@sacpremierpm.com', website: 'sacpremierpm.com',
    address: '2100 Northgate Blvd, Sacramento, CA 95833', units: 480, tags: ['W9 sent', 'COI on file', 'Net-30'],
    nextFollowUp: inDays(12), people: ['pe1'],
    activities: [
      a('email', 'inbound', ago(1, 3), 'Confirmed we’re approved — send invoices to AP portal.', 'Approved'),
      a('call', 'outbound', ago(2, 5), 'Walked through COI + W9. She’s adding us today.', 'Positive'),
      a('email', 'outbound', ago(4), 'Sent COI, W9, and rate sheet.', 'Sent docs'),
    ],
  },
  {
    id: 'c10', company: 'Cornerstone Builders', contactName: 'Joe Russo', title: 'Owner / GC',
    type: 'Contractor', relStage: 'Active Source', source: 'Referral', starred: true,
    phone: '916-555-0511', email: 'joe@cornerstonebuilders.com', website: 'cornerstonebuilders.com',
    address: '845 Vernon St, Roseville, CA 95678', units: null, tags: ['First-call sub', '3 jobs YTD', 'Pays on time'],
    nextFollowUp: inDays(9), people: ['pe2', 'pe3'],
    activities: [
      a('call', 'inbound', ago(2, 1), 'Joe wants me on the Midtown duplex — no bid needed.', 'Job won'),
      a('call', 'outbound', ago(20), 'Checked in on his spring board.', 'Positive'),
    ],
  },
  {
    id: 'c11', company: 'Summit Construction Group', contactName: 'Alicia Reyes', title: 'Project Manager',
    type: 'Contractor', relStage: 'Pursuing', source: 'ConstructionConnect', starred: false,
    phone: '916-555-0540', email: 'areyes@summitcg.com', website: 'summitcg.com',
    address: '5601 Stanford Ranch Rd, Rocklin, CA 95765', units: null, tags: ['Invited to bid TI work'],
    nextFollowUp: inDays(4), people: ['pe4'],
    activities: [
      a('email', 'inbound', ago(1), 'Invited us to bid an office TI — plans attached.', 'Bidding'),
      a('call', 'outbound', ago(2), 'Intro call, confirmed scope + walk-through.', 'Positive'),
    ],
  },
  {
    id: 'c3', company: 'Capitol Commercial Group', contactName: 'Priya Nair', title: 'Facilities Manager',
    type: 'Facilities / Building Owner', relStage: 'Pursuing', source: 'Cold outreach', starred: true,
    phone: '916-555-0203', email: 'p.nair@capitolcg.com', website: 'capitolcg.com',
    address: '1215 K St, Sacramento, CA 95814', units: null, tags: ['12 retail sites', 'After-hours work'],
    nextFollowUp: inDays(-1), people: [],
    activities: [
      a('email', 'outbound', ago(2), 'Intro email — service area + emergency response.', 'Sent'),
      a('voicemail', 'outbound', ago(4), 'Left VM with front desk for Priya.', 'No answer'),
    ],
  },
  {
    id: 'c2', company: 'Folsom Ridge HOA', contactName: 'Marcus Lee', title: 'Board President',
    type: 'HOA', relStage: 'Pursuing', source: 'Cold outreach', starred: false,
    phone: '916-555-0188', email: 'mlee@folsomridgehoa.org', website: 'folsomridgehoa.org',
    address: '300 Folsom Ridge Dr, Folsom, CA 95630', units: 210, tags: ['Bidding common-area lighting'],
    nextFollowUp: inDays(2), people: [],
    activities: [
      a('call', 'inbound', ago(0, 6), 'Wants a bid on clubhouse + lot lighting.', 'Hot lead'),
      a('email', 'outbound', ago(1), 'Sent intro + HOA references.', 'Sent'),
    ],
  },
  {
    id: 'c5', company: 'Riverside Realty Holdings', contactName: 'Janet Cho', title: 'Owner / Broker',
    type: 'Realtor', relStage: 'Prospect', source: 'Referral', starred: false,
    phone: '916-555-0311', email: 'janet@riversiderealty.com', website: 'riversiderealty.com',
    address: '1024 Jefferson Blvd, West Sacramento, CA 95691', units: null, tags: ['Flips + rentals'],
    nextFollowUp: inDays(5), people: [],
    activities: [a('note', 'outbound', ago(1), 'Referred by Marcus @ Folsom Ridge. ~10 flips/yr.', '')],
  },
  {
    id: 'c12', company: 'The Reyes Family', contactName: 'Sofia Reyes', title: 'Homeowner',
    type: 'Homeowner', relStage: 'Active Source', source: 'Referral', starred: false,
    phone: '916-555-0620', email: 'sofia.reyes@gmail.com', website: '',
    address: '88 Almond Orchard Ln, Fair Oaks, CA 95628', units: null, tags: ['Repeat — 3 jobs', 'Refers neighbors'],
    nextFollowUp: inDays(30), people: [],
    activities: [
      a('text', 'inbound', ago(5), '“My neighbor needs a panel upgrade — gave them your number.”', 'Referral'),
      a('call', 'outbound', ago(40), 'Wrapped the kitchen circuits, all good.', 'Job done'),
    ],
  },
  {
    id: 'c8', company: 'Old Town Builders', contactName: 'Hank Morrow', title: 'Project Lead',
    type: 'Contractor', relStage: 'Not a Fit', source: 'Cold outreach', starred: false,
    phone: '916-555-0401', email: 'hank@oldtownbuilders.com', website: 'oldtownbuilders.com',
    address: '120 Lincoln Way, Auburn, CA 95603', units: null, tags: ['Has in-house electrician'],
    nextFollowUp: null, people: [],
    activities: [a('call', 'outbound', ago(11), 'Has an in-house guy. Keep warm for overflow.', 'Not now')],
  },
];

// ======== PEOPLE (individuals; company optional; attach to projects) ========
export const SEED_PEOPLE = [
  {
    id: 'pe1', name: 'Dana Whitfield', role: 'Maintenance Coordinator', companyId: 'c1', companyName: 'Sacramento Premier Property Mgmt',
    phone: '916-555-0142', email: 'dana@sacpremierpm.com', projectIds: ['p5'],
    secondaryPhone: '916-555-0143', altEmail: 'dwhitfield@gmail.com',
    address: { street: '4820 Marconi Ave', city: 'Carmichael', state: 'CA', zip: '95608' },
    tags: ['Net-30', 'Fast payer', 'Dispatches after-hours'],
    notes: 'Prefers a text before arrival. Approves anything under $500 on the spot; larger jobs need a written quote to the regional manager.',
    lifetime: 12400, since: '2023',
    activities: [
      pa('call', 'inbound', ago(1), 'Dispatch — unit 4B has a dead circuit, needs same-day.', 'Job', 'p5'),
      pa('text', 'outbound', ago(1, 2), 'On my way, ETA 30 min.', 'Replied', 'p5'),
    ],
  },
  {
    id: 'pe2', name: 'Joe Russo', role: 'Owner / GC', companyId: 'c10', companyName: 'Cornerstone Builders',
    phone: '916-555-0511', email: 'joe@cornerstonebuilders.com', projectIds: ['p1'],
    altEmail: 'joe.russo.gc@gmail.com',
    address: { street: '1180 Front St, Ste 200', city: 'Sacramento', state: 'CA', zip: '95814' },
    tags: ['Repeat GC', 'Preferred-sub target'],
    notes: 'Runs 3–4 jobs a year. Wants a single point of contact and clean as-builts.',
    lifetime: 86500, since: '2021',
    activities: [
      pa('call', 'inbound', ago(2), 'Wants me on the Midtown duplex, start next week.', 'Job won', 'p1'),
    ],
  },
  {
    id: 'pe3', name: 'Mike Dolan', role: 'Superintendent', companyId: 'c10', companyName: 'Cornerstone Builders',
    phone: '916-555-0517', email: 'mike@cornerstonebuilders.com', projectIds: ['p1'],
    activities: [
      pa('text', 'inbound', ago(0, 4), '“Rough-in inspection passed — you’re clear to insulate.”', 'Cleared', 'p1'),
      pa('call', 'outbound', ago(3), 'Coordinated panel location with framing.', 'Aligned', 'p1'),
    ],
  },
  {
    id: 'pe4', name: 'Alicia Reyes', role: 'Project Manager', companyId: 'c11', companyName: 'Summit Construction Group',
    phone: '916-555-0540', email: 'areyes@summitcg.com', projectIds: ['p2'],
    activities: [
      pa('email', 'inbound', ago(1), 'Sent the revised plan set for the TI bid.', 'Bidding', 'p2'),
    ],
  },
  // ---- third parties: not one of your relationship companies ----
  {
    id: 'pe5', name: 'Raj Patel', role: 'Project Engineer', companyId: null, companyName: 'Voltrix Electric (sub-contractor)',
    phone: '916-555-0710', email: 'raj@voltrix.com', city: 'Sacramento', projectIds: ['p4'],
    activities: [
      pa('email', 'outbound', ago(2), 'Sent our load calcs for the shared service.', 'Sent', 'p4'),
      pa('call', 'inbound', ago(4), 'Coordinating the gear split on the mixed-use.', 'Aligned', 'p4'),
    ],
  },
  {
    id: 'pe6', name: 'Carla Nguyen', role: 'Interior Designer', companyId: null, companyName: 'Nguyen Design Studio (3rd party)',
    phone: '916-555-0733', email: 'carla@nguyendesign.com', city: 'Sacramento', projectIds: ['p4'],
    activities: [
      pa('email', 'inbound', ago(3), 'Lighting spec + fixture schedule for the lobby.', 'Specs', 'p4'),
      pa('note', 'outbound', ago(3, 2), 'She prefers to approve fixture mockups before order.', '', 'p4'),
    ],
  },
  {
    id: 'pe7', name: 'Frank Mendez', role: 'Building Inspector', companyId: null, companyName: 'City of Sacramento',
    phone: '916-555-0099', email: 'fmendez@cityofsacramento.org', city: 'Sacramento', projectIds: ['p4', 'p2', 'p1'],
    activities: [
      pa('call', 'inbound', ago(0, 6), 'Rough-in passed on Riverpark — sign-off posted.', 'Passed', 'p4'),
      pa('note', 'outbound', ago(8), 'Wants the panel schedule taped inside the cover on TIs.', '', 'p2'),
      pa('call', 'outbound', ago(20), 'Scheduled final for the Midtown duplex.', 'Scheduled', 'p1'),
    ],
  },
  {
    id: 'pe8', name: 'Dr. Ellis Wong', role: 'Geotechnical Engineer', companyId: null, companyName: 'GeoSolve Engineering',
    phone: '916-555-0744', email: 'ewong@geosolve.com', city: 'Roseville', projectIds: ['p4'],
    activities: [
      pa('email', 'inbound', ago(6), 'Soils report — note the high water table near grid C for the ground rods.', 'FYI', 'p4'),
    ],
  },
];

// ======== EXISTING CUSTOMERS (plain client records — kept simple, NOT forced
// through the relationship pipeline; they keep their own status) ========
export const SEED_CUSTOMERS = [
  { id: 'cu1', name: 'Mark Bennett', type: 'Homeowner', status: 'Active', phone: '916-555-0810', email: 'bennett.home@gmail.com', city: 'Carmichael' },
  { id: 'cu2', name: 'Greg Tanaka', type: 'Homeowner', status: 'Inactive', phone: '916-555-0822', email: 'gtanaka@outlook.com', city: 'Roseville' },
  { id: 'cu3', name: 'Linda Park', type: 'Homeowner', status: 'Lead', phone: '916-555-0838', email: 'lpark.home@gmail.com', city: 'Sacramento' },
];

// ======== BIDS / JOBS ========
export const SEED_BIDS = [
  { id: 'b1', title: 'Clubhouse + lot lighting', who: 'Folsom Ridge HOA', type: 'HOA', value: 18500, stage: 'Bidding', source: 'Cold outreach', due: inDays(2), contactId: 'c2' },
  {
    id: 'b2', title: 'Office TI — 6,000 sf', who: 'Summit Construction Group', type: 'Contractor',
    value: 54000, stage: 'Bidding', source: 'ConstructionConnect', due: inDays(5), contactId: 'c11',
    detail: {
      address: '2410 Sunrise Ave, Roseville, CA', estCost: 40500, status: 'Bid submitted — awaiting award',
      competitors: '2 other electricians bidding',
      scope: '6,000 sf office tenant improvement — complete electrical: lighting, power, two 100A subpanels, low-voltage rough-in, conference A/V power, and emergency egress lighting.',
      lineItems: [
        { label: '(2) 100A subpanels + feeders', amount: 9200 }, { label: 'Lighting — 78 fixtures + controls', amount: 16800 },
        { label: 'Branch power + devices', amount: 13400 }, { label: 'Low-voltage rough-in (data/AV)', amount: 5200 },
        { label: 'Emergency / egress lighting', amount: 3600 }, { label: 'Permit + inspections', amount: 2200 },
        { label: 'Labor + contingency', amount: 3600 },
      ],
      documents: [{ name: 'Plans — Summit Office TI.pdf', type: 'Plans' }, { name: 'Bid — Stalwart Electric v2.pdf', type: 'Proposal' }, { name: 'Scope clarifications — RFI #2.pdf', type: 'RFI' }],
      photos: [{ caption: 'Existing panel room', tone: 'from-slate-600 to-slate-800' }, { caption: 'Open ceiling — rough-in path', tone: 'from-zinc-600 to-zinc-800' }, { caption: 'Conference area', tone: 'from-stone-600 to-stone-800' }],
      people: [{ name: 'Alicia Reyes', role: 'GC — Summit (PM)', phone: '916-555-0540' }],
      timeline: [{ at: ago(2), label: 'Bid submitted via ConstructionConnect', kind: 'bid' }, { at: ago(4), label: 'Site walk-through with Alicia', kind: 'note' }, { at: ago(6), label: 'Invited to bid — plans received', kind: 'lead' }],
    },
  },
  { id: 'b3', title: 'Warehouse lighting retrofit', who: 'Delta Logistics Park', type: 'Facilities / Building Owner', value: 67000, stage: 'Bidding', source: 'PlanHub', due: inDays(4), contactId: null },
  { id: 'b4', title: 'Panel upgrade', who: 'Miller residence', type: 'Homeowner', value: 4200, stage: 'Bidding', source: 'Website', due: inDays(3), contactId: null },
  { id: 'b5', title: 'Apartment rewire (8 units)', who: 'Sierra Vista Mgmt', type: 'Property Management', value: 22000, stage: 'Lead', source: 'Vendor list', due: inDays(6), contactId: null },
  {
    id: 'b6', title: 'Retail TI — site #4', who: 'Capitol Commercial Group', type: 'Facilities / Building Owner',
    value: 42000, stage: 'Lead', source: 'Cold outreach', due: null, contactId: 'c3',
    detail: {
      address: '8821 Folsom Blvd, Suite 4, Sacramento, CA', estCost: 31500, status: 'New lead — awaiting plans',
      nextStep: 'Get plan set from architect, then schedule a site walk',
      scope: 'Tenant-improvement electrical for a 3,200 sf retail conversion — new 200A subpanel, lighting layout, 14 branch circuits, signage power, EV-ready conduit.',
      lineItems: [
        { label: '200A subpanel + feeders', amount: 6800 }, { label: 'Recessed + track lighting (42 fixtures)', amount: 12400 },
        { label: 'Branch circuits (14)', amount: 9800 }, { label: 'Signage + exterior power', amount: 4200 },
        { label: 'EV-ready rough-in', amount: 3600 }, { label: 'Permit + inspection', amount: 1800 }, { label: 'Labor contingency', amount: 3400 },
      ],
      documents: [{ name: 'RFP — Capitol CG.pdf', type: 'RFP' }],
      photos: [{ caption: 'Storefront — site #4', tone: 'from-slate-600 to-slate-800' }, { caption: 'Existing service', tone: 'from-zinc-600 to-zinc-800' }],
      people: [{ name: 'Priya Nair', role: 'Facilities Manager', phone: '916-555-0203' }],
      timeline: [{ at: ago(0, 4), label: 'Requested architect plan set', kind: 'note' }, { at: ago(1), label: 'Cold outreach to Priya converted into an RFP', kind: 'lead' }],
    },
  },
  { id: 'b7', title: 'Common-area panels', who: 'Greenhaven HOA', type: 'HOA', value: 9400, stage: 'Lead', source: 'Cold outreach', due: null, contactId: null },
  { id: 'b8', title: 'Midtown duplex build-out', who: 'Cornerstone Builders', type: 'Contractor', value: 38000, stage: 'Scheduled', source: 'Repeat GC', due: null, contactId: 'c10' },
  { id: 'b9', title: 'Service upgrade', who: 'Ramirez residence', type: 'Homeowner', value: 2800, stage: 'Scheduled', source: 'Referral', due: null, contactId: null },
  { id: 'b10', title: 'Tenant turn rewire — Oak Park', who: 'Northgate Residential', type: 'Property Management', value: 3100, stage: 'Won', source: 'Vendor list', due: null, contactId: null },
  { id: 'b11', title: 'EV charger install', who: 'Park residence', type: 'Homeowner', value: 1900, stage: 'Won', source: 'Website', due: null, contactId: null },
  {
    id: 'b12', title: 'Kitchen remodel circuits', who: 'Cornerstone Builders', type: 'Contractor',
    value: 7600, stage: 'Won', source: 'Repeat GC', due: null, contactId: 'c10',
    detail: {
      address: '1455 Midtown Ave, Sacramento, CA', estCost: 5180, status: 'Won — scheduled to start',
      awardedDate: ago(4), startDate: inDays(6), deposit: 2280,
      scope: 'Kitchen-remodel electrical — dedicated appliance circuits, under-cabinet + island lighting, panel additions, GFCI/AFCI to code.',
      lineItems: [
        { label: 'Dedicated appliance circuits (5)', amount: 2400 }, { label: 'Under-cabinet + island lighting', amount: 1900 },
        { label: 'Panel additions / breakers', amount: 1500 }, { label: 'GFCI/AFCI + devices', amount: 900 }, { label: 'Permit', amount: 400 }, { label: 'Labor', amount: 500 },
      ],
      documents: [{ name: 'Signed proposal.pdf', type: 'Contract' }, { name: 'Deposit invoice — $2,280.pdf', type: 'Invoice' }, { name: 'Permit — City of Sacramento.pdf', type: 'Permit' }, { name: 'Certificate of Insurance.pdf', type: 'COI' }],
      photos: [{ caption: 'Kitchen — before', tone: 'from-amber-700 to-stone-800' }, { caption: 'Panel — existing', tone: 'from-slate-600 to-slate-800' }, { caption: 'Island rough-in', tone: 'from-zinc-600 to-zinc-800' }, { caption: 'Under-cabinet runs', tone: 'from-neutral-600 to-neutral-800' }],
      people: [{ name: 'Joe Russo', role: 'GC — Cornerstone (Owner)', phone: '916-555-0511' }],
      timeline: [{ at: ago(3), label: 'Deposit received ($2,280)', kind: 'won' }, { at: ago(4), label: 'Awarded by Joe @ Cornerstone — no competitive bid', kind: 'won' }, { at: ago(6), label: 'Proposal sent', kind: 'bid' }],
    },
  },
  { id: 'b13', title: 'Office build-out electrical', who: 'Old Town Builders', type: 'Contractor', value: 51000, stage: 'Lost', source: 'ConstructionConnect', due: null, contactId: 'c8' },
  { id: 'b14', title: 'Whole-home rewire', who: 'Nguyen residence', type: 'Homeowner', value: 16000, stage: 'Lost', source: 'Website', due: null, contactId: null },
];
